import { Copy } from "lucide-react";
import { toast } from "sonner";
import { btnGhost } from "./ui";

const BASE = process.env.REACT_APP_BACKEND_URL;

const CPP = `// NCTelemetry.cpp — drop into the game module. Requires "HTTP" + "Json" in Build.cs.
// Reads the Tester ID the player pasted in Settings → Testing (mirrored into NCUser.ini as [Testing] TesterToken=...).
#include "HttpModule.h"
#include "Interfaces/IHttpRequest.h"
#include "Interfaces/IHttpResponse.h"
#include "GenericPlatform/GenericPlatformMisc.h"
#include "HAL/PlatformMemory.h"
#include "RHI.h"
#include "RenderCore.h"
#include "Engine/Engine.h"

static const FString NC_HUB = TEXT("${BASE}");

static void NCPost(const FString& Path, const TSharedRef<FJsonObject>& Body, const FString& Token)
{
    FString Payload; auto W = TJsonWriterFactory<>::Create(&Payload); FJsonSerializer::Serialize(Body, W);
    auto Req = FHttpModule::Get().CreateRequest();
    Req->SetURL(NC_HUB + Path); Req->SetVerb(TEXT("POST"));
    Req->SetHeader(TEXT("Content-Type"), TEXT("application/json"));
    Req->SetHeader(TEXT("X-Tester-Token"), Token);
    Req->SetContentAsString(Payload); Req->ProcessRequest();
}

// 1) Once per launch (after the player links a Tester ID) — PC specs + build + settings.
void UNCTelemetry::ReportSpecs(const FString& Token, const FString& SessionId)
{
    auto B = MakeShared<FJsonObject>();
    B->SetStringField("session_id", SessionId);
    B->SetStringField("os", FPlatformMisc::GetOSVersion());
    B->SetStringField("cpu", FPlatformMisc::GetCPUBrand());
    B->SetNumberField("cpu_cores", FPlatformMisc::NumberOfCoresIncludingHyperthreads());
    B->SetStringField("gpu", GRHIAdapterName);
    B->SetStringField("gpu_driver", GRHIAdapterUserDriverVersion);
    B->SetNumberField("ram_mb", FPlatformMemory::GetConstants().TotalPhysicalGB * 1024);
    B->SetNumberField("vram_mb", GRHIGlobals.VideoMemory ? GRHIGlobals.VideoMemory / (1024*1024) : 0); // or RHIGetAvailableResources
    FIntPoint Res = GEngine->GameViewport->Viewport->GetSizeXY();
    B->SetStringField("resolution", FString::Printf(TEXT("%dx%d"), Res.X, Res.Y));
    B->SetStringField("build_version", FApp::GetBuildVersion());
    auto S = MakeShared<FJsonObject>(); S->SetNumberField("sg.ResolutionQuality", Scalability::GetQualityLevels().ResolutionQuality);
    S->SetNumberField("sg.ShadowQuality", Scalability::GetQualityLevels().ShadowQuality); B->SetObjectField("settings", S);
    NCPost(TEXT("/api/testing/telemetry/specs"), B, Token);
}

// 2) Every 30 s while in a world — buffer samples, flush in batches of up to 200.
void UNCTelemetry::Sample(const FString& Zone, int32 Players, float PingMs, float PacketLoss, float ServerTick)
{
    extern ENGINE_API float GAverageFPS; extern ENGINE_API float GAverageMS;
    auto S = MakeShared<FJsonObject>();
    S->SetStringField("t", FDateTime::UtcNow().ToIso8601());
    S->SetNumberField("fps", GAverageFPS);
    S->SetNumberField("frame_ms", GAverageMS);
    S->SetNumberField("game_ms", FPlatformTime::ToMilliseconds(GGameThreadTime));
    S->SetNumberField("render_ms", FPlatformTime::ToMilliseconds(GRenderThreadTime));
    S->SetNumberField("gpu_ms", FPlatformTime::ToMilliseconds(RHIGetGPUFrameCycles()));
    S->SetNumberField("rhi_ms", FPlatformTime::ToMilliseconds(GRHIThreadTime));
    S->SetNumberField("ping_ms", PingMs); S->SetNumberField("packet_loss", PacketLoss); S->SetNumberField("server_tick", ServerTick);
    S->SetNumberField("players", Players); S->SetStringField("zone", Zone);
    FVector P = GetPawnLocation(); S->SetNumberField("x", P.X); S->SetNumberField("y", P.Y); S->SetNumberField("z", P.Z);
    FTextureMemoryStats TM; RHIGetTextureMemoryStats(TM);
    S->SetNumberField("vram_used_mb", TM.AllocatedMemorySize / (1024*1024)); S->SetNumberField("vram_total_mb", TM.TotalGraphicsMemory / (1024*1024));
    auto Mem = FPlatformMemory::GetStats();
    S->SetNumberField("ram_used_mb", Mem.UsedPhysical / (1024*1024)); S->SetNumberField("ram_total_mb", Mem.TotalPhysical / (1024*1024));
    S->SetNumberField("hitches", HitchesSinceLastSample); HitchesSinceLastSample = 0;   // count frames > 60 ms in Tick
    S->SetNumberField("draw_calls", GNumDrawCallsRHI[0]);
    S->SetNumberField("shader_compiles", ShaderCompilesSinceLastSample); ShaderCompilesSinceLastSample = 0;
    Buffer.Add(S);
    if (Buffer.Num() >= 4) Flush();   // ~2 min batches
}

void UNCTelemetry::Flush()
{
    auto B = MakeShared<FJsonObject>(); B->SetStringField("session_id", SessionId); B->SetStringField("build_version", FApp::GetBuildVersion());
    B->SetStringField("server_name", CurrentServerName);
    TArray<TSharedPtr<FJsonValue>> Arr; for (auto& S : Buffer) Arr.Add(MakeShared<FJsonValueObject>(S)); B->SetArrayField("samples", Arr); Buffer.Reset();
    NCPost(TEXT("/api/testing/telemetry/samples"), B, Token);
}

// 3) Events: /bug from chat, crash-on-relaunch (25 s after rejoin), disconnect, kick.
void UNCTelemetry::Event(const FString& Type, const FString& Message, const FString& LogTail)
{
    auto B = MakeShared<FJsonObject>(); B->SetStringField("session_id", SessionId); B->SetStringField("type", Type); // crash|bug|disconnect|kick|hitch|note
    B->SetStringField("message", Message); B->SetStringField("zone", CurrentZone); B->SetNumberField("players", CurrentPlayers);
    B->SetStringField("log_tail", LogTail.Right(20000)); NCPost(TEXT("/api/testing/telemetry/events"), B, Token);
}`;

const ROWS = [
  ["POST /api/testing/telemetry/specs", "once per launch", "os, cpu, cpu_cores, gpu, gpu_driver, ram_mb, vram_mb, resolution, display_hz, storage, build_version, settings{}, session_id → returns session_id"],
  ["POST /api/testing/telemetry/samples", "every ~30 s, batched (≤200)", "session_id, build_version, server_name, samples[]: fps, fps_1low, frame_ms, game_ms, render_ms, gpu_ms, rhi_ms, ping_ms, packet_loss, server_tick, players, zone, x,y,z, vram_used_mb, vram_total_mb, ram_used_mb, ram_total_mb, cpu_pct, gpu_pct, hitches, draw_calls, streaming_mb, shader_compiles"],
  ["POST /api/testing/telemetry/events", "on /bug, crash relaunch, disconnect, kick", "type, message, zone, x,y,z, players, fps, ping_ms, details{}, log_tail (≤20k chars)"],
  ["GET /api/testing/me", "on link", "X-Tester-Token header → tester status + program (use to validate the pasted ID and show name in-game)"],
  ["window.ue.gamebridge.perf()", "optional, CEF side", "If the client exposes a JSON perf snapshot {fps, frame_ms, game_ms, render_ms, gpu_ms, zone, players, vram_used_mb, vram_total_mb, ram_used_mb, hitches}, the in-game menu probe forwards it automatically every 2 minutes — zero extra HTTP code."],
];

/* The contract between the UE5 client and the hub — everything the game team needs to wire telemetry. */
export function TestingIntegration() {
  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Copied"); };
  return (
    <div className="flex flex-col gap-5">
      <div className="border border-white/10 bg-black/40 p-5">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-400">UE5 client → hub contract</p>
        <p className="font-mono text-[11px] text-zinc-400 mt-2 leading-relaxed">Auth is one header, <code className="text-nc-orange">X-Tester-Token</code>, holding the Tester ID the player pasted under Settings → Testing (the menu already mirrors settings into NCUser.ini — read <code>[Testing] TesterToken</code>). Consent is enforced server-side: a paused tester gets 403 and nothing is stored. Samples auto-expire after 60 days. The hub classifies every session (GPU-bound / game-thread / render-thread / VRAM / RAM / hitching / network / server-tick) and ranks fixes in Overview.</p>
        <table className="w-full mt-4 font-mono text-[10px]"><thead><tr className="text-zinc-500 text-left"><th className="py-1 pr-3">Endpoint</th><th className="pr-3">When</th><th>Fields</th></tr></thead>
          <tbody>{ROWS.map((r) => <tr key={r[0]} className="border-t border-white/5 align-top"><td className="py-2 pr-3 text-nc-orange whitespace-nowrap">{r[0]}</td><td className="pr-3 text-zinc-400 whitespace-nowrap">{r[1]}</td><td className="text-zinc-300 leading-relaxed">{r[2]}</td></tr>)}</tbody></table>
      </div>
      <div className="border border-white/10 bg-black/40 p-5">
        <div className="flex items-center justify-between"><p className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-400">Reference implementation (C++, UE 5.x)</p><button data-testid="integration-copy-cpp" onClick={() => copy(CPP)} className={btnGhost}><Copy className="w-3 h-3" /> Copy</button></div>
        <pre data-testid="integration-cpp" className="mt-3 max-h-[520px] overflow-auto bg-black/60 border border-white/10 p-4 font-mono text-[10px] text-zinc-300 whitespace-pre leading-relaxed">{CPP}</pre>
        <p className="font-mono text-[10px] text-zinc-500 mt-3 leading-relaxed">Hitch counting: in Tick, <code>if (DeltaSeconds &gt; 0.06f) ++HitchesSinceLastSample;</code>. Zone: your streaming-level / district name. Ping + loss: <code>PlayerState-&gt;GetPingInMilliseconds()</code> and <code>NetConnection-&gt;GetAverageLag() / InPacketsLost</code>. Server tick: replicate <code>GetWorld()-&gt;GetDeltaSeconds()</code> from the server once per second.</p>
      </div>
    </div>
  );
}
